import { useForm, zodResolver } from '@mantine/form';
import useTranslation from 'next-translate/useTranslation';
import React, { useCallback, useEffect } from 'react';
import { z } from 'zod';

import useOpenIntercom from '~/components/Intercom/useOpenItercom';
import { mailToSupport } from '~/components/utils/mailToSupport';

import useForgotPassword from '../hooks/useForgotPassword';
import type { ForgotPasswordFormValues } from '../interfaces/forgotPassword.interface';

import ForgotPasswordForm from './PasswordActions/ForgotPasswordForm';
import ResetPasswordConfirmation from './PasswordActions/ResetPasswordConfirmation';

type ForgotPasswordProps = {
  heading?: string;
  title: string;
  subtitle?: string;
  isReset?: boolean;
  email?: string;
};

export function ForgotPassword({ title, subtitle, isReset, email, heading }: ForgotPasswordProps) {
  const { t } = useTranslation('auth');
  const { openIntercom, isIntercomAvailable } = useOpenIntercom();

  const form = useForm<ForgotPasswordFormValues>({
    initialValues: {
      email: '',
    },
    validate: zodResolver(
      z.object({
        email: z.coerce.string().trim().email().min(1, t('forgotPassword.errors.minLength')),
      })
    ),
    validateInputOnBlur: true,
  });

  const onSupportLinkClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (isIntercomAvailable) {
      openIntercom(`I'm having trouble with the Forgotten Password flow.`);
    } else {
      mailToSupport({
        subject: 'Not able to log in',
        body: `Hello, Splash Support - I'm having trouble with the Forgotten Password flow.`,
      });
    }
  }, [isIntercomAvailable, openIntercom]);

  const { handleForgotPassword, isLoading, isLinkSent } = useForgotPassword(isReset);

  useEffect(() => {
    if (email && isReset) {
      form.setFieldValue('email', email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, isReset]);

  if (isLinkSent) {
    return <ResetPasswordConfirmation email={form.values.email} />;
  }

  return (
    <ForgotPasswordForm
      isReset={isReset}
      isFormLoading={isLoading}
      heading={heading}
      title={title}
      subtitle={subtitle}
      emailFieldProps={form.getInputProps('email')}
      handleFormSubmit={form.onSubmit(() => {
        handleForgotPassword({
          email: form.values.email,
        });
      })}
      handleSupportLinkClick={onSupportLinkClick}
    />
  );
}
